import React, { useState, useMemo, useCallback, useEffect } from "react";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  StandardizedTable,
  TableColumn,
  formatNumber,
  TrendIndicator,
  type TableRow,
} from "@/components/ui/standardized-table";
import { TrendingUp, TrendingDown, Minus, BarChart3 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useSEOCards } from "@/hooks/useSEOCards";
import { useSEODevices } from "@/hooks/useSEODevices";
import { useLandingPageData } from "@/hooks/useLandingPageData";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import FeatureSectionPaywall from "@/components/common/FeatureSectionPaywall";
import { SLUGS } from "@/lib/feature-gates";
import BrandTagsInput from "@/components/ui/brand-tags-input";
import {
  LineChart as TremorLineChart,
  BarChart as TremorBarChart,
} from "@tremor/react";
import {
  formatCurrency,
  formatPercent,
  formatSignedPercent,
} from "@/utils/numberFormat";
import type { TableQueryDTO } from "@/types/table-query";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { linhaDePalavraChave } from "@/lib/linha-de-palavra-chave";
import {
  CANAL_ORGANICO,
  linhaDePaginaDeEntrada,
  rodapeDePaginasDeEntrada,
} from "@/lib/linha-de-pagina-de-entrada";
import type {
  SeoClicksPositionChartData,
  SeoDeviceChartItem,
  SeoDeviceTableItem,
  SeoImpressionsCtrChartData,
  SeoSessionsChartData,
} from "@/backend-sdk/client/types.gen";

// Os dados da página são carregados diretamente via endpoints; nenhum mock local é utilizado.

type SEOSessionsChartData = SeoSessionsChartData;
type SEOClicksPositionChartData = SeoClicksPositionChartData;
type SEOImpressionsCTRChartData = SeoImpressionsCtrChartData;
type SEODeviceTableItem = SeoDeviceTableItem;
type SEODeviceChartItem = SeoDeviceChartItem;
type SeoMetricKey = "impressions" | "clicks" | "ctr" | "posicaoMedia";
type SeoScalar = number | string | null | undefined;

interface SessionsChartRow extends TableRow {
  data: string;
  Sessões: number;
  "Sessões Orgânicas": number;
}

interface ClicksPositionChartRow extends TableRow {
  data: string;
  clicks: number;
  posicaoMedia: number;
}

interface ImpressionsCtrChartRow extends TableRow {
  data: string;
  impressions: number;
  ctr: number;
}

interface UnifiedSeoChartRow extends TableRow {
  data: string;
  impressions: number;
  clicks: number;
  ctr: number;
  posicaoMedia: number;
}

interface DeviceTableRow extends TableRow {
  categoria: string;
  impressoes: number | null;
  impressoesChange: number | null;
  siteCTR: number | null;
  siteCTRChange: number | null;
  clicks: number | null;
  clicksChange: number | null;
  posicaoMedia: number | null;
  posicaoMediaChange: number | null;
}

interface SeoMetricConfig {
  key: SeoMetricKey;
  label: string;
  color: string;
}

interface LabeledSeoMetricRow extends TableRow {
  data: string;
}

interface RechartsTooltipEntry {
  dataKey?: string | number;
  value?: string | number;
  color?: string;
}

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent?: number;
}

/**
 * A cor e o ícone da tendência que a API publica. `neutral` — variação zero, ou dois períodos
 * sem dado — não é queda: sai cinza e sem seta para baixo (M-102).
 */
const corDaTendencia = (tendencia: string | null | undefined): string =>
  tendencia === "up"
    ? "text-success-soft"
    : tendencia === "down"
      ? "text-error-soft"
      : "text-muted-foreground";

const IconeDaTendencia = ({
  tendencia,
  className,
}: {
  tendencia: string | null | undefined;
  className: string;
}) =>
  tendencia === "up" ? (
    <TrendingUp className={className} />
  ) : tendencia === "down" ? (
    <TrendingDown className={className} />
  ) : (
    <Minus className={className} />
  );

const buildLabeledSeoMetricRows = (
  rows: UnifiedSeoChartRow[],
  metrics: SeoMetricConfig[],
): LabeledSeoMetricRow[] =>
  rows.map((row) => {
    const mapped: LabeledSeoMetricRow = { data: row.data };
    for (const metric of metrics) {
      mapped[metric.label] = row[metric.key];
    }
    return mapped;
  });

const seoScalarNumber = (value: SeoScalar): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }

  return null;
};

/**
 * O que cada bloco da página precisa. Os cards orgânicos e a comparação de canais saem do
 * GA4; cliques, impressões, CTR, posição e dispositivos saem do Search Console.
 */
const FONTES: Readonly<
  Record<"ga4" | "searchConsole", readonly RequisitoDeFonte[]>
> = {
  ga4: ["ga4"],
  searchConsole: ["search_console"],
};

// Colunas para a tabela de palavras-chave
const renderNumericValue = (
  value: unknown,
  formatter: (numeric: number) => string,
  fontes: readonly RequisitoDeFonte[],
): React.ReactNode => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return (
      <ValorIndisponivel className="text-muted-foreground" fontes={fontes} />
    );
  }
  return <span>{formatter(value)}</span>;
};

const renderDeltaCell =
  (fontes: readonly RequisitoDeFonte[]) =>
  (value: unknown): React.ReactNode => {
    const change = typeof value === "number" ? value : null;
    if (change === null || Number.isNaN(change)) {
      return (
        <ValorIndisponivel className="text-muted-foreground" fontes={fontes} />
      );
    }

    // Usa TrendIndicator padronizado (já respeita "Sem comparação")
    return <TrendIndicator value={change} />;
  };

const colunasPalavrasChave: TableColumn[] = [
  { key: "query", header: "Query", align: "left" },
  {
    key: "clicks",
    header: "Clicks",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => formatNumber(numeric),
        FONTES.searchConsole,
      ),
  },
  {
    key: "clicksChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "impressoes",
    header: "Impressões",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => formatNumber(numeric),
        FONTES.searchConsole,
      ),
  },
  {
    key: "impressoesChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "siteCTR",
    header: "Site CTR",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => `${Number(numeric).toFixed(2)}%`,
        FONTES.searchConsole,
      ),
  },
  {
    key: "siteCTRChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "posicaoMedia",
    header: "Posição média",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => numeric.toFixed(2),
        FONTES.searchConsole,
      ),
  },
  {
    key: "posicaoMediaChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
];

const colunasComparacaoCanais: TableColumn[] = [
  {
    key: "grupoCanal",
    header: "Grupo de canais padrão da sessão",
    align: "left",
  },
  {
    key: "sessoes",
    header: "Sessões",
    align: "right",
    render: (value) =>
      renderNumericValue(value, (numeric) => formatNumber(numeric), FONTES.ga4),
  },
  {
    key: "txConversao",
    header: "TX Conversão",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => `${Number(numeric).toFixed(2)}%`,
        FONTES.ga4,
      ),
  },
  {
    key: "pedidos",
    header: "Pedidos",
    align: "right",
    render: (value) =>
      renderNumericValue(value, (numeric) => formatNumber(numeric), FONTES.ga4),
  },
  {
    key: "pedidosChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.ga4),
  },
  {
    key: "ticketMedio",
    header: "TM",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => `R$ ${numeric.toFixed(2)}`,
        FONTES.ga4,
      ),
  },
  {
    key: "ticketMedioChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.ga4),
  },
  {
    key: "receitaTotal",
    header: "Receita total",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => `R$ ${formatNumber(numeric)}`,
        FONTES.ga4,
      ),
  },
  {
    key: "receitaTotalChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.ga4),
  },
];

// Colunas para a tabela de dispositivos
const colunasDispositivo: TableColumn[] = [
  { key: "categoria", header: "Categoria do Dispositivo", align: "left" },
  {
    key: "impressoes",
    header: "Impressões",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => formatNumber(numeric),
        FONTES.searchConsole,
      ),
  },
  {
    key: "impressoesChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "siteCTR",
    header: "Site CTR",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => `${Number(numeric).toFixed(2)}%`,
        FONTES.searchConsole,
      ),
  },
  {
    key: "siteCTRChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "clicks",
    header: "Clicks",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => formatNumber(numeric),
        FONTES.searchConsole,
      ),
  },
  {
    key: "clicksChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
  {
    key: "posicaoMedia",
    header: "Posição média",
    align: "right",
    render: (value) =>
      renderNumericValue(
        value,
        (numeric) => numeric.toFixed(2),
        FONTES.searchConsole,
      ),
  },
  {
    key: "posicaoMediaChange",
    header: "% Δ",
    align: "right",
    render: renderDeltaCell(FONTES.searchConsole),
  },
];

const mapComparisonParam = (comparisonType?: string) => {
  return comparisonType === "previousYear"
    ? "previous_year"
    : "previous_period";
};

/**
 * <auditoria-18> Aqui moravam `getBrandTermsStorageKey`, `normalizeBrandTerm`,
 * `normalizeBrandMatchValue` e `deriveDefaultBrandTerms` — a chave do `localStorage` onde os
 * termos de marca viviam e o derivador que a tela usava quando não havia nenhum.
 *
 * Os quatro saíram. Os termos são da **loja** e ficam no servidor, e a derivação é de lá,
 * feita a partir do domínio da propriedade do Search Console. Ter duas derivações era o
 * defeito: esta deduzia do nome e do slug da conta, a do servidor deduzia do domínio, e as
 * duas discordavam no mesmo card — 80 cliques de marca por uma e 63 pela outra, medidos na
 * conta Magnata na janela do relatório.
 */

const SEOPage = () => {
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { selectedAccount } = useAccount();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const { isEnabled: isSeoFeatureEnabled, planName } = usePlanFeatures();
  // <auditoria-18> A tela não guarda mais os termos: quem os guarda é a loja, no servidor.
  // O que sobra aqui é um contador que muda a cada gravação, para que os recortes que
  // dependem deles sejam refeitos — salvar um termo não muda parâmetro nenhum das duas
  // rotas, e sem isto a página seguiria mostrando o recorte anterior.
  const [brandTermsNonce, setBrandTermsNonce] = useState(0);
  const [seoChartOptions, setSeoChartOptions] = useState({
    impressions: true,
    ctr: true,
    clicks: false,
    posicaoMedia: false,
  });
  const [seoClicksChartOptions, setSeoClicksChartOptions] = useState({
    clicks: true,
    posicaoMedia: true,
    impressions: false,
    ctr: false,
  });
  const [marcaImpressoesChartOptions, setMarcaImpressoesChartOptions] =
    useState({
      impressions: true,
      clicks: false,
      ctr: false,
      posicaoMedia: false,
    });
  const [marcaClicksChartOptions, setMarcaClicksChartOptions] = useState({
    clicks: true,
    impressions: false,
    ctr: false,
    posicaoMedia: false,
  });
  const [marcaCtrChartOptions, setMarcaCtrChartOptions] = useState({
    ctr: true,
    impressions: false,
    clicks: false,
    posicaoMedia: false,
  });
  const [marcaPosicaoChartOptions, setMarcaPosicaoChartOptions] = useState({
    posicaoMedia: true,
    impressions: false,
    clicks: false,
    ctr: false,
  });

  // Chamado quando o `PUT` volta: os termos gravados mudaram, e os recortes que eles
  // filtram têm de ser buscados de novo.
  const handleBrandTermsSaved = useCallback(() => {
    setBrandTermsNonce((anterior) => anterior + 1);
  }, []);

  const filterParams = useMemo(() => {
    if (!filterState) return null;
    const start = formatDateToISO(filterState.dateRange.startDate);
    const end = formatDateToISO(filterState.dateRange.endDate);
    const comparison = mapComparisonParam(filterState.comparison?.type);
    return { start, end, comparison };
  }, [filterState]);

  const startDate = filterParams?.start ?? "";
  const endDate = filterParams?.end ?? "";
  const comparison = filterParams?.comparison ?? "previous_period";
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    undefined;
  const campaignCustomerUuid = customerUuid || null;
  // Os endpoints de SEO já consolidam por customer_uuid; não há seleção por conta/integração.
  const accountId = undefined;
  const campaignAccountId = selectedAccount?.account_id ?? undefined;
  // Os endpoints de SEO já consolidão por customer_uuid; não há seleção por conta/integração.

  // Obter dados dos cards SEO usando o hook personalizado
  const {
    data: seoCardsData,
    loading: seoCardsLoading,
    error: seoCardsError,
  } = useSEOCards(
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    campaignFiltersNonce,
    brandTermsNonce,
  );

  // Dispositivos (Search Console) – endpoint dedicado. As duas chamadas diferem só no
  // recorte: a primeira é o site inteiro, a segunda é a marca — e <auditoria-18> é `brand_only`
  // que diz isso, em vez de a diferença ser "mandei ou não mandei os termos".
  const { data: seoDevicesData, loading: seoDevicesLoading } = useSEODevices(
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    false,
    1,
    10,
    campaignFiltersNonce,
    brandTermsNonce,
  );

  const { data: seoBrandDevicesData, loading: seoBrandDevicesLoading } =
    useSEODevices(
      startDate,
      endDate,
      comparison,
      customerUuid,
      accountId,
      true,
      1,
      10,
      campaignFiltersNonce,
      brandTermsNonce,
    );

  // Dados reais obtidos do endpoint (quando disponíveis) ou dados mockados temporários
  const dadosComparacaoCanais = useMemo(
    () => seoCardsData?.comparacao_canais_table?.data ?? [],
    [seoCardsData],
  );

  const dadosSessoesGraficos = useMemo(
    () =>
      (seoCardsData?.sessoes_chart_data ?? []).map(
        (row: SEOSessionsChartData): SessionsChartRow => ({
          data: row.data,
          Sessões: row.sessoes ?? 0,
          "Sessões Orgânicas": row.sessoesOrganicas ?? 0,
        }),
      ),
    [seoCardsData],
  );

  const dadosClicksPosicao = useMemo(() => {
    const rows = seoCardsData?.clicks_position_chart_data ?? [];
    return rows.map(
      (row: SEOClicksPositionChartData): ClicksPositionChartRow => ({
        data: row.data,
        clicks: typeof row.clicks === "number" ? row.clicks : 0,
        posicaoMedia:
          typeof row.posicaoMedia === "number"
            ? parseFloat(row.posicaoMedia.toFixed(2))
            : 0,
      }),
    );
  }, [seoCardsData]);

  // Dados do gráfico Impressões x CTR (Search Console)
  const dadosImpressionsCTR = useMemo(() => {
    const rows = seoCardsData?.impressions_ctr_chart_data ?? [];
    return rows.map(
      (row: SEOImpressionsCTRChartData): ImpressionsCtrChartRow => ({
        data: row.data,
        impressions: typeof row.impressions === "number" ? row.impressions : 0,
        ctr: typeof row.ctr === "number" ? parseFloat(row.ctr.toFixed(2)) : 0,
      }),
    );
  }, [seoCardsData]);

  // Dataset unificado: Impressões + CTR + Clicks + Posição Média (por data)
  const dadosUnificadoSEOChart = useMemo(() => {
    const impressRows = seoCardsData?.impressions_ctr_chart_data ?? [];
    const clicksRows = seoCardsData?.clicks_position_chart_data ?? [];
    const clicksMap = new Map<
      string,
      { clicks: number; posicaoMedia: number }
    >();
    for (const row of clicksRows) {
      clicksMap.set(row.data, {
        clicks: typeof row.clicks === "number" ? row.clicks : 0,
        posicaoMedia:
          typeof row.posicaoMedia === "number"
            ? parseFloat(row.posicaoMedia.toFixed(2))
            : 0,
      });
    }
    return impressRows.map(
      (row: SEOImpressionsCTRChartData): UnifiedSeoChartRow => {
        const extra = clicksMap.get(row.data) ?? { clicks: 0, posicaoMedia: 0 };
        return {
          data: row.data,
          impressions:
            typeof row.impressions === "number" ? row.impressions : 0,
          ctr: typeof row.ctr === "number" ? parseFloat(row.ctr.toFixed(2)) : 0,
          clicks: extra.clicks,
          posicaoMedia: extra.posicaoMedia,
        };
      },
    );
  }, [seoCardsData]);

  const handleSeoChartOptionChange = (option: keyof typeof seoChartOptions) => {
    setSeoChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleSeoClicksChartOptionChange = (
    option: keyof typeof seoClicksChartOptions,
  ) => {
    setSeoClicksChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleMarcaImpressoesChartOptionChange = (
    option: keyof typeof marcaImpressoesChartOptions,
  ) => {
    setMarcaImpressoesChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleMarcaClicksChartOptionChange = (
    option: keyof typeof marcaClicksChartOptions,
  ) => {
    setMarcaClicksChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleMarcaCtrChartOptionChange = (
    option: keyof typeof marcaCtrChartOptions,
  ) => {
    setMarcaCtrChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleMarcaPosicaoChartOptionChange = (
    option: keyof typeof marcaPosicaoChartOptions,
  ) => {
    setMarcaPosicaoChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const palavrasChaveTabela = useMemo(
    () =>
      (seoCardsData?.palavras_chaves_table?.data ?? []).map(
        linhaDePalavraChave,
      ),
    [seoCardsData],
  );

  /**
   * <M-232> O "Total Geral" que `GET /api/v1/seo/cards` publica em
   * `palavras_chaves_table.total_row`, agregado sobre **todas** as consultas do período.
   *
   * Sem ele, `StandardizedTable` monta o rodapé sozinha e soma coluna por coluna as dez
   * linhas que tem em mãos — `Site CTR` e `Posição média` inclusive, que não declaram `type`
   * e por isso são classificadas como número por `inferColumnType`. Medido na conta
   * auditada: CTR 121,52% e posição 33,97, contra os 1,04% e a posição 6,27 do conjunto.
   *
   * `null` (nenhuma consulta no período) devolve a tabela ao rodapé próprio, que sobre uma
   * tabela vazia não desenha nada.
   */
  const palavrasChaveTotal = useMemo(() => {
    const total = seoCardsData?.palavras_chaves_table?.total_row;
    return total ? linhaDePalavraChave(total) : undefined;
  }, [seoCardsData]);

  /**
   * <auditoria-18> A tabela da aba MARCA, agora vinda da rota.
   *
   * Ela filtrava, aqui na tela, as **dez** linhas que a tabela geral publica — as dez
   * consultas mais clicadas do site inteiro. Uma consulta de marca fora desse top 10
   * simplesmente sumia da aba, enquanto continuava dentro dos cards de marca logo acima:
   * medido na conta Magnata na janela do relatório, 128 consultas casavam a marca e cinco
   * delas estavam entre as dez, de modo que a tabela somava 73 cliques e 3.146 impressões
   * onde os cards somavam 80 e 3.364. É o padrão B da auditoria, e o servidor passou a
   * publicar o recorte inteiro em `palavras_chaves_marca_table`.
   */
  const palavrasChaveDaMarca = useMemo(
    () =>
      (seoCardsData?.palavras_chaves_marca_table?.data ?? []).map(
        linhaDePalavraChave,
      ),
    [seoCardsData],
  );

  /** O rodapé daquela tabela, agregado sobre **todas** as consultas de marca. */
  const palavrasChaveDaMarcaTotal = useMemo(() => {
    const total = seoCardsData?.palavras_chaves_marca_table?.total_row;
    return total ? linhaDePalavraChave(total) : undefined;
  }, [seoCardsData]);

  // Dados de dispositivos a partir do endpoint dedicado
  const dadosDispositivo = useMemo(
    () => seoDevicesData?.charts?.impressions || [],
    [seoDevicesData],
  );
  const dadosCTRDispositivo = useMemo(
    () => seoDevicesData?.charts?.ctr || [],
    [seoDevicesData],
  );
  const dadosClicksDispositivo = useMemo(
    () => seoDevicesData?.charts?.clicks || [],
    [seoDevicesData],
  );
  const dadosPosicaoDispositivo = useMemo(
    () => seoDevicesData?.charts?.position || [],
    [seoDevicesData],
  );
  const dadosDispositivoMarca = useMemo(
    () => seoBrandDevicesData?.charts?.impressions || [],
    [seoBrandDevicesData],
  );
  const dadosCTRDispositivoMarca = useMemo(
    () => seoBrandDevicesData?.charts?.ctr || [],
    [seoBrandDevicesData],
  );
  const dadosClicksDispositivoMarca = useMemo(
    () => seoBrandDevicesData?.charts?.clicks || [],
    [seoBrandDevicesData],
  );
  const dadosPosicaoDispositivoMarca = useMemo(
    () => seoBrandDevicesData?.charts?.position || [],
    [seoBrandDevicesData],
  );

  // Gráficos de dispositivo: exibir valor real da métrica (não percentual de participação)
  const deviceChartsConfig = useMemo(
    () => [
      {
        title: "IMPRESSÕES POR DISPOSITIVO (SC)",
        data: dadosDispositivo,
        formatValue: (v: number) => v.toLocaleString("pt-BR"),
      },
      {
        title: "CTR POR DISPOSITIVO (SC)",
        data: dadosCTRDispositivo,
        formatValue: (v: number) => formatPercent(v),
      },
      {
        title: "CLICKS POR DISPOSITIVO (SC)",
        data: dadosClicksDispositivo,
        formatValue: (v: number) => v.toLocaleString("pt-BR"),
      },
      {
        title: "POSIÇÃO MÉDIA POR DISPOSITIVO (SC)",
        data: dadosPosicaoDispositivo,
        formatValue: (v: number) => v.toFixed(1),
      },
    ],
    [
      dadosDispositivo,
      dadosCTRDispositivo,
      dadosClicksDispositivo,
      dadosPosicaoDispositivo,
    ],
  );

  const brandDeviceChartsConfig = useMemo(
    () => [
      {
        title: "IMPRESSÕES POR DISPOSITIVO (SC)",
        data: dadosDispositivoMarca,
        formatValue: (v: number) => v.toLocaleString("pt-BR"),
      },
      {
        title: "CTR POR DISPOSITIVO (SC)",
        data: dadosCTRDispositivoMarca,
        formatValue: (v: number) => formatPercent(v),
      },
      {
        title: "CLICKS POR DISPOSITIVO (SC)",
        data: dadosClicksDispositivoMarca,
        formatValue: (v: number) => v.toLocaleString("pt-BR"),
      },
      {
        title: "POSIÇÃO MÉDIA POR DISPOSITIVO (SC)",
        data: dadosPosicaoDispositivoMarca,
        formatValue: (v: number) => v.toFixed(1),
      },
    ],
    [
      dadosDispositivoMarca,
      dadosCTRDispositivoMarca,
      dadosClicksDispositivoMarca,
      dadosPosicaoDispositivoMarca,
    ],
  );

  // Mapear dados da tabela para o formato esperado pelas colunas existentes
  const dadosTabelaDispositivo = useMemo(() => {
    const rows = seoDevicesData?.table?.data || [];
    return rows.map(
      (r: SEODeviceTableItem): DeviceTableRow => ({
        categoria: r.categoria,
        impressoes: seoScalarNumber(r.impressoes),
        impressoesChange:
          typeof r.impressoes_change_percentage === "number"
            ? r.impressoes_change_percentage
            : null,
        siteCTR:
          seoScalarNumber(r.siteCTR) === null
            ? null
            : parseFloat((seoScalarNumber(r.siteCTR) ?? 0).toFixed(2)),
        siteCTRChange:
          typeof r.siteCTR_change_percentage === "number"
            ? r.siteCTR_change_percentage
            : null,
        clicks: seoScalarNumber(r.clicks),
        clicksChange:
          typeof r.clicks_change_percentage === "number"
            ? r.clicks_change_percentage
            : null,
        posicaoMedia:
          seoScalarNumber(r.posicaoMedia) === null
            ? null
            : parseFloat((seoScalarNumber(r.posicaoMedia) ?? 0).toFixed(2)),
        posicaoMediaChange:
          typeof r.posicaoMedia_change_percentage === "number"
            ? r.posicaoMedia_change_percentage
            : null,
      }),
    );
  }, [seoDevicesData]);

  const dadosTabelaDispositivoMarca = useMemo(() => {
    const rows = seoBrandDevicesData?.table?.data || [];
    return rows.map(
      (r: SEODeviceTableItem): DeviceTableRow => ({
        categoria: r.categoria,
        impressoes: seoScalarNumber(r.impressoes),
        impressoesChange:
          typeof r.impressoes_change_percentage === "number"
            ? r.impressoes_change_percentage
            : null,
        siteCTR:
          seoScalarNumber(r.siteCTR) === null
            ? null
            : parseFloat((seoScalarNumber(r.siteCTR) ?? 0).toFixed(2)),
        siteCTRChange:
          typeof r.siteCTR_change_percentage === "number"
            ? r.siteCTR_change_percentage
            : null,
        clicks: seoScalarNumber(r.clicks),
        clicksChange:
          typeof r.clicks_change_percentage === "number"
            ? r.clicks_change_percentage
            : null,
        posicaoMedia:
          seoScalarNumber(r.posicaoMedia) === null
            ? null
            : parseFloat((seoScalarNumber(r.posicaoMedia) ?? 0).toFixed(2)),
        posicaoMediaChange:
          typeof r.posicaoMedia_change_percentage === "number"
            ? r.posicaoMedia_change_percentage
            : null,
      }),
    );
  }, [seoBrandDevicesData]);

  // ===== Landing Pages (Páginas de Entrada) =====
  const [lpPage, setLpPage] = useState(1);
  const [lpTableQuery, setLpTableQuery] = useState<TableQueryDTO | undefined>();
  const lpPageSize = 25;

  const {
    data: landingPageData,
    totals: landingPageTotals,
    pagination: lpPagination,
    loading: lpLoading,
  } = useLandingPageData({
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    page: lpPage,
    pageSize: lpPageSize,
    tableQuery: lpTableQuery,
    // <auditoria-19> Esta aba é de SEO: o universo dela é a busca orgânica, e não o
    // tráfego inteiro. O rótulo é o mesmo `Organic Search` que os cards de sessões e
    // pedidos orgânicos logo acima usam — a tabela e os cards passam a somar o mesmo.
    filterChannelGroup: CANAL_ORGANICO,
  });

  /**
   * A página volta ao começo quando o conjunto muda debaixo dela.
   *
   * Sem isto, um `lpPage = 3` sobrevive a um estreitamento do período e a rota responde uma
   * página fora de faixa — `data: []` sobre um conjunto cheio. O recorte orgânico do item 19
   * encolheu o conjunto em ~3,3× (57 páginas contra 187 na conta auditada), o que trouxe esse
   * estado para perto de qualquer troca de janela.
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLpPage(1);
  }, [startDate, endDate, comparison, customerUuid, accountId]);

  const landingPageTableData = useMemo(
    () => landingPageData.map(linhaDePaginaDeEntrada),
    [landingPageData],
  );

  /**
   * <auditoria-20> O rodapé é o da rota, e vai como `total` — não como mais uma linha.
   *
   * Ele entrava em `data`, e aí o `StandardizedTable` somava as linhas da página **mais**
   * ele para montar o rodapé automático: 25.175 sessões viravam 46.289 na conta auditada.
   * Com `queryMode="server"` a soma automática nem existe, e o rodapé é o do conjunto.
   */
  const landingPageTotalRow = useMemo(
    () => rodapeDePaginasDeEntrada(landingPageTotals, lpPagination.total_items),
    [landingPageTotals, lpPagination.total_items],
  );

  // Colunas da tabela Página de Entrada
  const colunasLandingPage: TableColumn[] = useMemo(
    () => [
      {
        key: "paginaDestino",
        header: "Página de destino",
        align: "left" as const,
      },
      {
        key: "sessoes",
        header: "Sessões",
        align: "right" as const,
        sortable: true,
        render: (value) =>
          renderNumericValue(value, (n) => formatNumber(n), FONTES.ga4),
      },
      {
        key: "sessoesChange",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell(FONTES.ga4),
      },
      {
        key: "pedidos",
        header: "Pedidos",
        align: "right" as const,
        sortable: true,
        render: (value) =>
          renderNumericValue(value, (n) => formatNumber(n), FONTES.ga4),
      },
      {
        key: "pedidosChange",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell(FONTES.ga4),
      },
      {
        key: "valorSessao",
        header: "Valor Sessão",
        align: "right" as const,
        sortable: true,
        // ⚠️ `formatCurrency`, e não `formatNumber`: esta é a mesma tabela de
        // `/dashboard/pages`, que sempre formatou as duas colunas de dinheiro como dinheiro.
        // `formatInteger` **trunca para o inteiro** — a receita orgânica da conta auditada
        // chega do GA4 como 898,999999 e saía `R$ 898`, um real abaixo dos R$ 899 do Looker.
        // Como moeda ela sai `R$ 898,99`; o centavo que falta é a truncagem de duas casas,
        // que é escolha do painel inteiro.
        render: (value) =>
          renderNumericValue(value, formatCurrency, FONTES.ga4),
      },
      {
        key: "valorSessaoChange",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell(FONTES.ga4),
      },
      {
        key: "txConversao",
        header: "TX Conversão",
        align: "right" as const,
        sortable: true,
        render: (value) =>
          renderNumericValue(
            value,
            (n) => `${n.toFixed(2).replace(".", ",")}%`,
            FONTES.ga4,
          ),
      },
      {
        key: "txConversaoChange",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell(FONTES.ga4),
      },
      {
        key: "receitaTotal",
        header: "Receita total",
        align: "right" as const,
        sortable: true,
        render: (value) =>
          renderNumericValue(value, formatCurrency, FONTES.ga4),
      },
      {
        key: "receitaTotalChange",
        header: "% Δ",
        align: "right" as const,
        render: renderDeltaCell(FONTES.ga4),
      },
    ],
    [],
  );

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">SEO</h1>
        <p className="text-muted-foreground">
          Análise completa das métricas de SEO e Search Console
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={campaignCustomerUuid}
            accountId={campaignAccountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* === ABAS === */}
      <Tabs defaultValue="home" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="home">HOME</TabsTrigger>
          <TabsTrigger value="marca">MARCA</TabsTrigger>
          <TabsTrigger value="paginas-entrada">PÁGINAS DE ENTRADA</TabsTrigger>
        </TabsList>

        {/* ====== ABA HOME ====== */}
        <TabsContent value="home" className="space-y-6 mt-4">
          {/* Filtro de Marca */}
          <Card className="card-kpi-secondary">
            <CardHeader>
              <CardTitle>TERMOS DE MARCA</CardTitle>
            </CardHeader>
            <CardContent>
              <BrandTagsInput
                customerUuid={customerUuid}
                onSaved={handleBrandTermsSaved}
                placeholder="Digite um termo de marca e pressione Enter..."
              />
            </CardContent>
          </Card>

          {/* PRIMEIRA LINHA: CARDS PRINCIPAIS */}
          <div className="grid gap-6 md:grid-cols-3">
            {/* Pedidos Orgânicos */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">Pedidos Orgânicos</span>
                    <HelpTooltip
                      content={getKpiTooltip("Pedidos Orgânicos")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-3xl" />
                ) : seoCardsData?.pedidos_organicos ? (
                  <>
                    <div className="flex items-baseline gap-2 mb-4 min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.pedidos_organicos.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-3xl font-light text-muted-foreground"
                            fontes={FONTES.ga4}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.ga4}
                            className="text-3xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.pedidos_organicos.valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={40}
                              breathingRoomPx={24}
                              ariaLabel="Pedidos orgânicos"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.pedidos_organicos.valor,
                        ) !== null &&
                        typeof seoCardsData.pedidos_organicos
                          .change_percentage === "number" &&
                        seoCardsData.pedidos_organicos.trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.pedidos_organicos.trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.pedidos_organicos.trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.pedidos_organicos.change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          Representatividade
                        </span>
                        <span className="text-sm font-medium">
                          {seoCardsData.pedidos_organicos.representatividade}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          Pedidos Totais
                        </span>
                        <span className="text-sm font-medium">
                          {seoScalarNumber(
                            seoCardsData.pedidos_organicos.pedidos_totais,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            formatNumber(
                              seoScalarNumber(
                                seoCardsData.pedidos_organicos.pedidos_totais,
                              ) ?? 0,
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-3xl font-light text-muted-foreground"
                    fontes={FONTES.ga4}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>

            {/* Sessões Orgânicas */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">Sessões Orgânicas</span>
                    <HelpTooltip
                      content={getKpiTooltip("Sessões Orgânicas")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-3xl" />
                ) : seoCardsData?.sessoes_organicas ? (
                  <>
                    <div className="flex items-baseline gap-2 mb-4 min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.sessoes_organicas.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-3xl font-light text-muted-foreground"
                            fontes={FONTES.ga4}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.ga4}
                            className="text-3xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.sessoes_organicas.valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={40}
                              breathingRoomPx={24}
                              ariaLabel="Sessões orgânicas"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.sessoes_organicas.valor,
                        ) !== null &&
                        typeof seoCardsData.sessoes_organicas
                          .change_percentage === "number" &&
                        seoCardsData.sessoes_organicas.trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.sessoes_organicas.trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.sessoes_organicas.trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.sessoes_organicas.change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          Representatividade
                        </span>
                        <span className="text-sm font-medium">
                          {seoCardsData.sessoes_organicas.representatividade}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          Sessões Totais
                        </span>
                        <span className="text-sm font-medium">
                          {seoScalarNumber(
                            seoCardsData.sessoes_organicas.sessoes_totais,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            formatNumber(
                              seoScalarNumber(
                                seoCardsData.sessoes_organicas.sessoes_totais,
                              ) ?? 0,
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-3xl font-light text-muted-foreground"
                    fontes={FONTES.ga4}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>

            {/* Taxa Conversão Orgânica */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">Taxa Conversão Orgânica</span>
                    <HelpTooltip
                      content={getKpiTooltip("Taxa Conversão Orgânica")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-3xl" />
                ) : seoCardsData?.taxa_conversao_organica ? (
                  <>
                    <div className="flex items-baseline gap-2 mb-4 min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.taxa_conversao_organica.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-3xl font-light text-muted-foreground"
                            fontes={FONTES.ga4}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.ga4}
                            className="text-3xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.taxa_conversao_organica.valor,
                                ) ?? 0,
                              )}
                              format={(n) => `${Number(n).toFixed(2)}%`}
                              className="font-light text-foreground"
                              maxFontPx={40}
                              breathingRoomPx={24}
                              ariaLabel="Taxa de conversão orgânica"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.taxa_conversao_organica.valor,
                        ) !== null &&
                        typeof seoCardsData.taxa_conversao_organica
                          .change_percentage === "number" &&
                        seoCardsData.taxa_conversao_organica
                          .trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.taxa_conversao_organica
                                .trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.taxa_conversao_organica
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.taxa_conversao_organica
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          TX Conversão Geral
                        </span>
                        <span className="text-sm font-medium">
                          {seoScalarNumber(
                            seoCardsData.taxa_conversao_organica
                              .taxa_conversao_geral,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            `${seoScalarNumber(seoCardsData.taxa_conversao_organica.taxa_conversao_geral) ?? 0}%`
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-3xl font-light text-muted-foreground"
                    fontes={FONTES.ga4}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* SEGUNDA LINHA: CARDS SEARCH CONSOLE */}
          <div className="grid gap-6 md:grid-cols-4">
            {/* Total de Clicks */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">
                      Total de Clicks (Search Console)
                    </span>
                    <HelpTooltip
                      content={getKpiTooltip(
                        "Total de Clicks (Search Console)",
                      )}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-2xl" />
                ) : seoCardsData?.total_clicks_search_console ? (
                  <>
                    <div className="flex flex-col items-start min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.total_clicks_search_console.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-2xl font-light text-muted-foreground"
                            fontes={FONTES.searchConsole}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-2xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.total_clicks_search_console
                                    .valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={30}
                              breathingRoomPx={18}
                              ariaLabel="Total de clicks (Search Console)"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.total_clicks_search_console.valor,
                        ) !== null &&
                        typeof seoCardsData.total_clicks_search_console
                          .change_percentage === "number" &&
                        seoCardsData.total_clicks_search_console
                          .trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.total_clicks_search_console
                                .trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-2xl font-light text-muted-foreground"
                    fontes={FONTES.searchConsole}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>

            {/* Total de Clicks MARCA */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">
                      Total de Clicks MARCA (Search Console)
                    </span>
                    <HelpTooltip
                      content={getKpiTooltip(
                        "Total de Clicks MARCA (Search Console)",
                      )}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-2xl" />
                ) : seoCardsData?.total_clicks_marca_search_console ? (
                  <>
                    <div className="flex flex-col items-start min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.total_clicks_marca_search_console.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-2xl font-light text-muted-foreground"
                            fontes={FONTES.searchConsole}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-2xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.total_clicks_marca_search_console
                                    .valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={30}
                              breathingRoomPx={18}
                              ariaLabel="Total de clicks marca (Search Console)"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.total_clicks_marca_search_console.valor,
                        ) !== null &&
                        typeof seoCardsData.total_clicks_marca_search_console
                          .change_percentage === "number" &&
                        seoCardsData.total_clicks_marca_search_console
                          .trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.total_clicks_marca_search_console
                                .trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_marca_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-2xl font-light text-muted-foreground"
                    fontes={FONTES.searchConsole}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>

            {/* Impressões */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">
                      Impressões (Search Console)
                    </span>
                    <HelpTooltip
                      content={getKpiTooltip("Impressões (Search Console)")}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-2xl" />
                ) : seoCardsData?.impressoes_search_console ? (
                  <>
                    <div className="flex flex-col items-start min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.impressoes_search_console.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-2xl font-light text-muted-foreground"
                            fontes={FONTES.searchConsole}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-2xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.impressoes_search_console.valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={30}
                              breathingRoomPx={18}
                              ariaLabel="Impressões (Search Console)"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.impressoes_search_console.valor,
                        ) !== null &&
                        typeof seoCardsData.impressoes_search_console
                          .change_percentage === "number" &&
                        seoCardsData.impressoes_search_console
                          .trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.impressoes_search_console
                                .trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-2xl font-light text-muted-foreground"
                    fontes={FONTES.searchConsole}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>

            {/* Impressões MARCA */}
            <Card className="card-kpi-primary">
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">
                      Impressões MARCA (Search Console)
                    </span>
                    <HelpTooltip
                      content={getKpiTooltip(
                        "Impressões MARCA (Search Console)",
                      )}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <CarregandoValor className="text-2xl" />
                ) : seoCardsData?.impressoes_marca_search_console ? (
                  <>
                    <div className="flex flex-col items-start min-w-0">
                      <div className="min-w-0 flex-1">
                        {seoScalarNumber(
                          seoCardsData.impressoes_marca_search_console.valor,
                        ) === null ? (
                          <ValorIndisponivel
                            className="text-2xl font-light text-muted-foreground"
                            fontes={FONTES.searchConsole}
                          />
                        ) : (
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-2xl font-light text-muted-foreground"
                          >
                            <KpiNumber
                              value={Number(
                                seoScalarNumber(
                                  seoCardsData.impressoes_marca_search_console
                                    .valor,
                                ) ?? 0,
                              )}
                              format={(n) => formatNumber(n)}
                              className="font-light text-foreground"
                              maxFontPx={30}
                              breathingRoomPx={18}
                              ariaLabel="Impressões marca (Search Console)"
                            />
                          </EstadoDoValor>
                        )}
                      </div>
                      {comparisonEnabled &&
                        seoScalarNumber(
                          seoCardsData.impressoes_marca_search_console.valor,
                        ) !== null &&
                        typeof seoCardsData.impressoes_marca_search_console
                          .change_percentage === "number" &&
                        seoCardsData.impressoes_marca_search_console
                          .trend_indicator && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(
                              seoCardsData.impressoes_marca_search_console
                                .trend_indicator,
                            )}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_marca_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  </>
                ) : (
                  <ValorIndisponivel
                    className="text-2xl font-light text-muted-foreground"
                    fontes={FONTES.searchConsole}
                    erro={seoCardsError !== null}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* TABELA DE PALAVRAS-CHAVE */}
          <StandardizedTable
            title="Palavras Chaves (Search Console)"
            columns={colunasPalavrasChave}
            data={palavrasChaveTabela}
            total={palavrasChaveTotal}
            loading={seoCardsLoading}
            className="mt-8"
          />

          {/* GRÁFICO IMPRESSÕES E SITE CTR (full-width) */}
          <Card className="card-kpi-secondary">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-center flex-1">
                  IMPRESSÕES E SITE CTR
                </CardTitle>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-muted-foreground"
                    >
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Métricas
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuCheckboxItem
                      checked={seoChartOptions.impressions}
                      onCheckedChange={() =>
                        handleSeoChartOptionChange("impressions")
                      }
                    >
                      Impressions
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoChartOptions.ctr}
                      onCheckedChange={() => handleSeoChartOptionChange("ctr")}
                    >
                      Site CTR
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoChartOptions.clicks}
                      onCheckedChange={() =>
                        handleSeoChartOptionChange("clicks")
                      }
                    >
                      Clicks
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoChartOptions.posicaoMedia}
                      onCheckedChange={() =>
                        handleSeoChartOptionChange("posicaoMedia")
                      }
                    >
                      Average Position
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const hasAnyActive =
                  seoChartOptions.impressions ||
                  seoChartOptions.ctr ||
                  seoChartOptions.clicks ||
                  seoChartOptions.posicaoMedia;
                if (!hasAnyActive)
                  return (
                    <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                      Selecione ao menos uma métrica
                    </div>
                  );

                const COLORS: Record<string, string> = {
                  impressions: "#64748b",
                  ctr: "#ef4444",
                  clicks: "#f59e0b",
                  posicaoMedia: "#22c55e",
                };
                const LABELS: Record<string, string> = {
                  impressions: "Impressões",
                  ctr: "Site CTR",
                  clicks: "Clicks",
                  posicaoMedia: "Average Position",
                };

                return (
                  <ScrollableChart minWidth="500px">
                    <ResponsiveContainer width="100%" height={288}>
                      <ComposedChart
                        data={dadosUnificadoSEOChart}
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(0,0,0,0.06)"
                        />
                        <XAxis
                          dataKey="data"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />

                        {/* Eixo Y Esquerdo: valores absolutos (Impressões, Clicks, Posição Média) */}
                        <YAxis
                          yAxisId="left"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={60}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `${(v / 1000).toLocaleString("pt-BR")}k`
                              : String(v)
                          }
                        />

                        {/* Eixo Y Direito: percentual (Site CTR) */}
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={50}
                          tickFormatter={(v: number) => `${v}%`}
                        />

                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                <p className="text-gray-500 text-xs mb-1">
                                  {label}
                                </p>
                                <table style={{ borderSpacing: "8px 2px" }}>
                                  <tbody>
                                    {payload.map((entry) => {
                                      const key = entry.dataKey as string;
                                      const formatted =
                                        key === "ctr"
                                          ? `${Number(entry.value).toFixed(2)}%`
                                          : key === "posicaoMedia"
                                            ? Number(entry.value).toFixed(1)
                                            : Number(
                                                entry.value,
                                              ).toLocaleString("pt-BR");
                                      return (
                                        <tr key={key}>
                                          <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                            <span
                                              className="inline-block w-2.5 h-2.5 rounded-full"
                                              style={{
                                                backgroundColor:
                                                  COLORS[key] || entry.color,
                                              }}
                                            />
                                            {LABELS[key] || key}:
                                          </td>
                                          <td className="text-right font-semibold text-gray-900 pl-3">
                                            {formatted}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          }}
                        />

                        <RechartsLegend
                          formatter={(value: string) => LABELS[value] || value}
                          wrapperStyle={{ fontSize: 12 }}
                        />

                        {/* Impressões e Clicks no eixo esquerdo */}
                        {seoChartOptions.impressions && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="impressions"
                            stroke={COLORS.impressions}
                            strokeWidth={2}
                            dot={false}
                            name="impressions"
                          />
                        )}
                        {seoChartOptions.clicks && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="clicks"
                            stroke={COLORS.clicks}
                            strokeWidth={2}
                            dot={false}
                            name="clicks"
                          />
                        )}

                        {/* Posição Média no eixo esquerdo */}
                        {seoChartOptions.posicaoMedia && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="posicaoMedia"
                            stroke={COLORS.posicaoMedia}
                            strokeWidth={2}
                            dot={false}
                            name="posicaoMedia"
                          />
                        )}

                        {/* Site CTR no eixo direito (percentual) */}
                        {seoChartOptions.ctr && (
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="ctr"
                            stroke={COLORS.ctr}
                            strokeWidth={2}
                            dot={false}
                            name="ctr"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ScrollableChart>
                );
              })()}
            </CardContent>
          </Card>

          {/* GRÁFICO CLICKS E POSIÇÃO MÉDIA (full-width) */}
          <Card className="card-kpi-secondary">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-center flex-1">
                  CLICKS E POSIÇÃO MÉDIA
                </CardTitle>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-muted-foreground"
                    >
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Métricas
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuCheckboxItem
                      checked={seoClicksChartOptions.clicks}
                      onCheckedChange={() =>
                        handleSeoClicksChartOptionChange("clicks")
                      }
                    >
                      Clicks
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoClicksChartOptions.posicaoMedia}
                      onCheckedChange={() =>
                        handleSeoClicksChartOptionChange("posicaoMedia")
                      }
                    >
                      Average Position
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoClicksChartOptions.impressions}
                      onCheckedChange={() =>
                        handleSeoClicksChartOptionChange("impressions")
                      }
                    >
                      Impressions
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={seoClicksChartOptions.ctr}
                      onCheckedChange={() =>
                        handleSeoClicksChartOptionChange("ctr")
                      }
                    >
                      Site CTR
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const hasAnyActive =
                  seoClicksChartOptions.clicks ||
                  seoClicksChartOptions.posicaoMedia ||
                  seoClicksChartOptions.impressions ||
                  seoClicksChartOptions.ctr;
                if (!hasAnyActive)
                  return (
                    <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                      Selecione ao menos uma métrica
                    </div>
                  );

                const COLORS: Record<string, string> = {
                  clicks: "#f59e0b",
                  posicaoMedia: "#22c55e",
                  impressions: "#64748b",
                  ctr: "#ef4444",
                };
                const LABELS: Record<string, string> = {
                  clicks: "Clicks",
                  posicaoMedia: "Posição Média",
                  impressions: "Impressões",
                  ctr: "Site CTR",
                };

                {
                  /* Determinar qual eixo direito mostrar: Posição Média tem prioridade, senão CTR */
                }
                const showRightPosition = seoClicksChartOptions.posicaoMedia;
                const showRightCtr =
                  seoClicksChartOptions.ctr && !showRightPosition;

                return (
                  <ScrollableChart minWidth="500px">
                    <ResponsiveContainer width="100%" height={288}>
                      <ComposedChart
                        data={dadosUnificadoSEOChart}
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(0,0,0,0.06)"
                        />
                        <XAxis
                          dataKey="data"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />

                        {/* Eixo Y Esquerdo: valores absolutos (Clicks, Impressões) */}
                        <YAxis
                          yAxisId="left"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={60}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `${(v / 1000).toLocaleString("pt-BR")}k`
                              : String(v)
                          }
                        />

                        {/* Eixo Y Direito: Posição Média (escala invertida: valores baixos = melhor posição) */}
                        {showRightPosition && (
                          <YAxis
                            yAxisId="rightPos"
                            orientation="right"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={50}
                            reversed
                            domain={["auto", "auto"]}
                            tickFormatter={(v: number) => v.toFixed(1)}
                          />
                        )}

                        {/* Eixo Y Direito: percentual (Site CTR) — quando Posição Média NÃO está ativa */}
                        {showRightCtr && (
                          <YAxis
                            yAxisId="rightCtr"
                            orientation="right"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={50}
                            tickFormatter={(v: number) => `${v}%`}
                          />
                        )}

                        {/* Eixo oculto para CTR quando Posição Média está ativa (para não quebrar o layout) */}
                        {seoClicksChartOptions.ctr && showRightPosition && (
                          <YAxis yAxisId="rightCtr" orientation="right" hide />
                        )}

                        {/* Eixo oculto para posicaoMedia quando NÃO está ativa */}
                        {!showRightPosition && (
                          <YAxis yAxisId="rightPos" orientation="right" hide />
                        )}

                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                <p className="text-gray-500 text-xs mb-1">
                                  {label}
                                </p>
                                <table style={{ borderSpacing: "8px 2px" }}>
                                  <tbody>
                                    {payload.map((entry) => {
                                      const key = entry.dataKey as string;
                                      const formatted =
                                        key === "ctr"
                                          ? `${Number(entry.value).toFixed(2)}%`
                                          : key === "posicaoMedia"
                                            ? Number(entry.value).toFixed(1)
                                            : Number(
                                                entry.value,
                                              ).toLocaleString("pt-BR");
                                      return (
                                        <tr key={key}>
                                          <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                            <span
                                              className="inline-block w-2.5 h-2.5 rounded-full"
                                              style={{
                                                backgroundColor:
                                                  COLORS[key] || entry.color,
                                              }}
                                            />
                                            {LABELS[key] || key}:
                                          </td>
                                          <td className="text-right font-semibold text-gray-900 pl-3">
                                            {formatted}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          }}
                        />

                        <RechartsLegend
                          formatter={(value: string) => LABELS[value] || value}
                          wrapperStyle={{ fontSize: 12 }}
                        />

                        {/* Clicks e Impressões no eixo esquerdo */}
                        {seoClicksChartOptions.clicks && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="clicks"
                            stroke={COLORS.clicks}
                            strokeWidth={2}
                            dot={false}
                            name="clicks"
                          />
                        )}
                        {seoClicksChartOptions.impressions && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="impressions"
                            stroke={COLORS.impressions}
                            strokeWidth={2}
                            dot={false}
                            name="impressions"
                          />
                        )}

                        {/* Posição Média no eixo direito dedicado */}
                        {seoClicksChartOptions.posicaoMedia && (
                          <Line
                            yAxisId="rightPos"
                            type="monotone"
                            dataKey="posicaoMedia"
                            stroke={COLORS.posicaoMedia}
                            strokeWidth={2}
                            dot={false}
                            name="posicaoMedia"
                          />
                        )}

                        {/* Site CTR no eixo direito (percentual) */}
                        {seoClicksChartOptions.ctr && (
                          <Line
                            yAxisId="rightCtr"
                            type="monotone"
                            dataKey="ctr"
                            stroke={COLORS.ctr}
                            strokeWidth={2}
                            dot={false}
                            name="ctr"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ScrollableChart>
                );
              })()}
            </CardContent>
          </Card>

          {/* TABELA GRUPO DE CANAIS PADRÃO DA SESSÃO */}
          <StandardizedTable
            title="Comparação do orgânico com outros canais"
            columns={colunasComparacaoCanais}
            data={dadosComparacaoCanais}
            loading={seoCardsLoading}
            className="mt-8"
          />

          {/* GRÁFICO SESSÕES X SESSÕES ORGÂNICAS (full-width, stacked bar) */}
          <Card className="card-kpi-secondary">
            <CardHeader>
              <CardTitle>Sessões x Sessões Orgânicas</CardTitle>
            </CardHeader>
            <CardContent>
              <TremorBarChart
                className="h-72"
                data={dadosSessoesGraficos}
                index="data"
                categories={["Sessões", "Sessões Orgânicas"]}
                colors={["amber", "red"]}
                valueFormatter={(value) => value.toLocaleString("pt-BR")}
                showLegend={true}
                showGridLines={true}
                showAnimation={true}
                stack={true}
              />
            </CardContent>
          </Card>

          {/* GRÁFICO SESSÕES ORGÂNICAS (full-width, line) */}
          <Card className="card-kpi-secondary">
            <CardHeader>
              <CardTitle>Sessões Orgânicas</CardTitle>
            </CardHeader>
            <CardContent>
              <TremorLineChart
                className="h-72"
                data={dadosSessoesGraficos}
                index="data"
                categories={["Sessões Orgânicas"]}
                colors={["amber"]}
                valueFormatter={(value) => value.toLocaleString("pt-BR")}
                showLegend={true}
                showGridLines={true}
                showAnimation={true}
              />
            </CardContent>
          </Card>

          {/* Gráficos de Dispositivos (Pizza) - rótulos em percentual de participação */}
          <div className="grid gap-6 md:grid-cols-4">
            {(() => {
              const PIE_COLORS = [
                "#3b82f6",
                "#10b981",
                "#f59e0b",
                "#ef4444",
                "#8b5cf6",
              ];
              const MIN_LABEL_PERCENT = 5;

              const createPieLabelRenderer =
                () =>
                ({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  percent,
                }: PieLabelProps) => {
                  const percentage = Number(percent || 0) * 100;
                  if (
                    !Number.isFinite(percentage) ||
                    percentage < MIN_LABEL_PERCENT
                  )
                    return null;
                  const RADIAN = Math.PI / 180;
                  const radius =
                    innerRadius + (outerRadius - innerRadius) * 0.55;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={12}
                      fontWeight={600}
                    >
                      {`${percentage.toFixed(1)}%`}
                    </text>
                  );
                };

              const renderPieLegend = (
                data: { name: string; value: number }[],
              ) => (
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2 text-xs">
                  {data.map((entry, i) => (
                    <span
                      key={entry.name}
                      className="inline-flex items-center gap-1 text-muted-foreground"
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{
                          backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                        }}
                      />
                      {entry.name}
                    </span>
                  ))}
                </div>
              );

              return deviceChartsConfig.map((chart) => (
                <Card className="card-kpi-secondary" key={chart.title}>
                  <CardHeader>
                    <CardTitle>{chart.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {seoDevicesLoading ? (
                      <div className="h-40 bg-muted animate-pulse rounded" />
                    ) : (
                      <>
                        <div className="mx-auto h-[200px] w-full max-w-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={chart.data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={85}
                                innerRadius={0}
                                label={createPieLabelRenderer()}
                                labelLine={false}
                                isAnimationActive={true}
                                stroke="hsl(var(--background))"
                                strokeWidth={2}
                              >
                                {chart.data.map(
                                  (_: SEODeviceChartItem, index: number) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={
                                        PIE_COLORS[index % PIE_COLORS.length]
                                      }
                                    />
                                  ),
                                )}
                              </Pie>
                              <RechartsTooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const entry = payload[0];
                                  const total = chart.data.reduce(
                                    (s, d) => s + d.value,
                                    0,
                                  );
                                  const pct =
                                    total > 0
                                      ? (
                                          (Number(entry.value) / total) *
                                          100
                                        ).toFixed(1)
                                      : "0";
                                  return (
                                    <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                                      <p className="font-medium text-foreground">
                                        {entry.name}
                                      </p>
                                      <p className="text-muted-foreground">
                                        {chart.formatValue?.(
                                          Number(entry.value),
                                        ) ?? entry.value}{" "}
                                        ({pct}%)
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        {renderPieLegend(chart.data)}
                      </>
                    )}
                  </CardContent>
                </Card>
              ));
            })()}
          </div>

          {/* Tabela de Dispositivos */}
          <StandardizedTable
            title="Categoria do Dispositivo"
            columns={colunasDispositivo}
            data={dadosTabelaDispositivo}
            loading={seoDevicesLoading}
            className="mt-8"
          />
        </TabsContent>

        {/* ====== ABA MARCA ====== */}
        <TabsContent value="marca" className="space-y-6 mt-4">
          <FeatureSectionPaywall
            featureSlug={SLUGS["analytics:seo-pagina-de-entrada"]}
            description="Análise de desempenho das páginas de entrada de SEO."
            planName={planName}
            isLocked={
              !isSeoFeatureEnabled(SLUGS["analytics:seo-pagina-de-entrada"])
            }
          >
            {/* KPI CARDS MARCA: 6 cards em linha */}
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {/* Total de Clicks (Search Console) */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">
                    Total de Clicks (Search Console)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.total_clicks_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.total_clicks_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.total_clicks_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={26}
                          breathingRoomPx={12}
                          ariaLabel="Total de clicks"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.total_clicks_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-xs ${corDaTendencia(seoCardsData.total_clicks_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_search_console
                                  .trend_indicator
                              }
                              className="w-3 h-3"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Total de Clicks MARCA (Search Console) */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">
                    Total de Clicks MARCA (Search Console)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.total_clicks_marca_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.total_clicks_marca_search_console
                              .valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.total_clicks_marca_search_console
                              .valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={26}
                          breathingRoomPx={12}
                          ariaLabel="Total de clicks marca"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.total_clicks_marca_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-xs ${corDaTendencia(seoCardsData.total_clicks_marca_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_marca_search_console
                                  .trend_indicator
                              }
                              className="w-3 h-3"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Representatividade Clicks */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">Representatividade</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.total_clicks_search_console &&
                    seoCardsData?.total_clicks_marca_search_console ? (
                    (() => {
                      const totalClicks =
                        Number(
                          seoCardsData.total_clicks_search_console.valor,
                        ) || 0;
                      const marcaClicks =
                        Number(
                          seoCardsData.total_clicks_marca_search_console.valor,
                        ) || 0;
                      const representatividade =
                        totalClicks > 0 ? (marcaClicks / totalClicks) * 100 : 0;
                      return (
                        <div className="flex flex-col items-start">
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-muted-foreground text-sm"
                          >
                            <span className="text-2xl font-light text-foreground">
                              {representatividade.toFixed(2).replace(".", ",")}%
                            </span>
                          </EstadoDoValor>
                        </div>
                      );
                    })()
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Impressões (Search Console) */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">
                    Impressões (Search Console)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.impressoes_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.impressoes_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.impressoes_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={26}
                          breathingRoomPx={12}
                          ariaLabel="Impressões"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.impressoes_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-xs ${corDaTendencia(seoCardsData.impressoes_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_search_console
                                  .trend_indicator
                              }
                              className="w-3 h-3"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Impressões MARCA (Search Console) */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">
                    Impressões MARCA (Search Console)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.impressoes_marca_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.impressoes_marca_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.impressoes_marca_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={26}
                          breathingRoomPx={12}
                          ariaLabel="Impressões marca"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.impressoes_marca_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-xs ${corDaTendencia(seoCardsData.impressoes_marca_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_marca_search_console
                                  .trend_indicator
                              }
                              className="w-3 h-3"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Representatividade Impressões */}
              <Card className="card-kpi-primary">
                <CardHeader className="pb-1">
                  <CardTitle className="text-2xs">Representatividade</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.impressoes_search_console &&
                    seoCardsData?.impressoes_marca_search_console ? (
                    (() => {
                      const totalImp =
                        seoScalarNumber(
                          seoCardsData.impressoes_search_console.valor,
                        ) ?? 0;
                      const marcaImp =
                        seoScalarNumber(
                          seoCardsData.impressoes_marca_search_console.valor,
                        ) ?? 0;
                      const representatividade =
                        totalImp > 0 ? (marcaImp / totalImp) * 100 : 0;
                      return (
                        <div className="flex flex-col items-start">
                          <EstadoDoValor
                            carregando={false}
                            disponivel
                            fontes={FONTES.searchConsole}
                            className="text-muted-foreground text-sm"
                          >
                            <span className="text-2xl font-light text-foreground">
                              {representatividade.toFixed(2).replace(".", ",")}%
                            </span>
                          </EstadoDoValor>
                        </div>
                      );
                    })()
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* GRÁFICO IMPRESSÕES (full-width) */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-center flex-1">
                    IMPRESSÕES
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Métricas
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuCheckboxItem
                        checked={marcaImpressoesChartOptions.impressions}
                        onCheckedChange={() =>
                          handleMarcaImpressoesChartOptionChange("impressions")
                        }
                      >
                        Impressões
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaImpressoesChartOptions.clicks}
                        onCheckedChange={() =>
                          handleMarcaImpressoesChartOptionChange("clicks")
                        }
                      >
                        Clicks
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaImpressoesChartOptions.ctr}
                        onCheckedChange={() =>
                          handleMarcaImpressoesChartOptionChange("ctr")
                        }
                      >
                        Site CTR
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaImpressoesChartOptions.posicaoMedia}
                        onCheckedChange={() =>
                          handleMarcaImpressoesChartOptionChange("posicaoMedia")
                        }
                      >
                        Average Position
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <div className="h-72 bg-muted animate-pulse rounded" />
                ) : (
                  (() => {
                    const allMetrics: SeoMetricConfig[] = [
                      {
                        key: "impressions",
                        label: "Impressões",
                        color: "amber",
                      },
                      { key: "clicks", label: "Clicks", color: "red" },
                      { key: "ctr", label: "Site CTR", color: "blue" },
                      {
                        key: "posicaoMedia",
                        label: "Average Position",
                        color: "violet",
                      },
                    ];
                    const active = allMetrics.filter(
                      (m) =>
                        marcaImpressoesChartOptions[
                          m.key as keyof typeof marcaImpressoesChartOptions
                        ],
                    );
                    if (active.length === 0)
                      return (
                        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                          Selecione ao menos uma métrica
                        </div>
                      );
                    const labeledData = buildLabeledSeoMetricRows(
                      dadosUnificadoSEOChart,
                      allMetrics,
                    );
                    return (
                      <TremorLineChart
                        className="h-72"
                        data={labeledData}
                        index="data"
                        categories={active.map((m) => m.label)}
                        colors={active.map((m) => m.color)}
                        valueFormatter={(value) =>
                          value.toLocaleString("pt-BR")
                        }
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    );
                  })()
                )}
              </CardContent>
            </Card>

            {/* GRÁFICO CLICKS (full-width) */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-center flex-1">CLICKS</CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Métricas
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuCheckboxItem
                        checked={marcaClicksChartOptions.clicks}
                        onCheckedChange={() =>
                          handleMarcaClicksChartOptionChange("clicks")
                        }
                      >
                        Clicks
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaClicksChartOptions.impressions}
                        onCheckedChange={() =>
                          handleMarcaClicksChartOptionChange("impressions")
                        }
                      >
                        Impressões
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaClicksChartOptions.ctr}
                        onCheckedChange={() =>
                          handleMarcaClicksChartOptionChange("ctr")
                        }
                      >
                        Site CTR
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaClicksChartOptions.posicaoMedia}
                        onCheckedChange={() =>
                          handleMarcaClicksChartOptionChange("posicaoMedia")
                        }
                      >
                        Average Position
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <div className="h-72 bg-muted animate-pulse rounded" />
                ) : (
                  (() => {
                    const allMetrics: SeoMetricConfig[] = [
                      { key: "clicks", label: "Clicks", color: "red" },
                      {
                        key: "impressions",
                        label: "Impressões",
                        color: "amber",
                      },
                      { key: "ctr", label: "Site CTR", color: "blue" },
                      {
                        key: "posicaoMedia",
                        label: "Average Position",
                        color: "violet",
                      },
                    ];
                    const active = allMetrics.filter(
                      (m) =>
                        marcaClicksChartOptions[
                          m.key as keyof typeof marcaClicksChartOptions
                        ],
                    );
                    if (active.length === 0)
                      return (
                        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                          Selecione ao menos uma métrica
                        </div>
                      );
                    const labeledData = buildLabeledSeoMetricRows(
                      dadosUnificadoSEOChart,
                      allMetrics,
                    );
                    return (
                      <TremorLineChart
                        className="h-72"
                        data={labeledData}
                        index="data"
                        categories={active.map((m) => m.label)}
                        colors={active.map((m) => m.color)}
                        valueFormatter={(value) =>
                          value.toLocaleString("pt-BR")
                        }
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    );
                  })()
                )}
              </CardContent>
            </Card>

            {/* GRÁFICO SITE CTR (full-width) */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-center flex-1">SITE CTR</CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Métricas
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuCheckboxItem
                        checked={marcaCtrChartOptions.ctr}
                        onCheckedChange={() =>
                          handleMarcaCtrChartOptionChange("ctr")
                        }
                      >
                        Site CTR
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaCtrChartOptions.impressions}
                        onCheckedChange={() =>
                          handleMarcaCtrChartOptionChange("impressions")
                        }
                      >
                        Impressões
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaCtrChartOptions.clicks}
                        onCheckedChange={() =>
                          handleMarcaCtrChartOptionChange("clicks")
                        }
                      >
                        Clicks
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaCtrChartOptions.posicaoMedia}
                        onCheckedChange={() =>
                          handleMarcaCtrChartOptionChange("posicaoMedia")
                        }
                      >
                        Average Position
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <div className="h-72 bg-muted animate-pulse rounded" />
                ) : (
                  (() => {
                    const allMetrics: SeoMetricConfig[] = [
                      { key: "ctr", label: "Site CTR", color: "blue" },
                      {
                        key: "impressions",
                        label: "Impressões",
                        color: "amber",
                      },
                      { key: "clicks", label: "Clicks", color: "red" },
                      {
                        key: "posicaoMedia",
                        label: "Average Position",
                        color: "violet",
                      },
                    ];
                    const active = allMetrics.filter(
                      (m) =>
                        marcaCtrChartOptions[
                          m.key as keyof typeof marcaCtrChartOptions
                        ],
                    );
                    if (active.length === 0)
                      return (
                        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                          Selecione ao menos uma métrica
                        </div>
                      );
                    const labeledData = buildLabeledSeoMetricRows(
                      dadosUnificadoSEOChart,
                      allMetrics,
                    );
                    return (
                      <TremorLineChart
                        className="h-72"
                        data={labeledData}
                        index="data"
                        categories={active.map((m) => m.label)}
                        colors={active.map((m) => m.color)}
                        valueFormatter={(value) =>
                          value.toLocaleString("pt-BR")
                        }
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    );
                  })()
                )}
              </CardContent>
            </Card>

            {/* GRÁFICO POSIÇÃO MÉDIA (full-width) */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-center flex-1">
                    POSIÇÃO MÉDIA
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Métricas
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuCheckboxItem
                        checked={marcaPosicaoChartOptions.posicaoMedia}
                        onCheckedChange={() =>
                          handleMarcaPosicaoChartOptionChange("posicaoMedia")
                        }
                      >
                        Average Position
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaPosicaoChartOptions.impressions}
                        onCheckedChange={() =>
                          handleMarcaPosicaoChartOptionChange("impressions")
                        }
                      >
                        Impressões
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaPosicaoChartOptions.clicks}
                        onCheckedChange={() =>
                          handleMarcaPosicaoChartOptionChange("clicks")
                        }
                      >
                        Clicks
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={marcaPosicaoChartOptions.ctr}
                        onCheckedChange={() =>
                          handleMarcaPosicaoChartOptionChange("ctr")
                        }
                      >
                        Site CTR
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <div className="h-72 bg-muted animate-pulse rounded" />
                ) : (
                  (() => {
                    const allMetrics: SeoMetricConfig[] = [
                      {
                        key: "posicaoMedia",
                        label: "Average Position",
                        color: "rose",
                      },
                      {
                        key: "impressions",
                        label: "Impressões",
                        color: "amber",
                      },
                      { key: "clicks", label: "Clicks", color: "red" },
                      { key: "ctr", label: "Site CTR", color: "blue" },
                    ];
                    const active = allMetrics.filter(
                      (m) =>
                        marcaPosicaoChartOptions[
                          m.key as keyof typeof marcaPosicaoChartOptions
                        ],
                    );
                    if (active.length === 0)
                      return (
                        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
                          Selecione ao menos uma métrica
                        </div>
                      );
                    const labeledData = buildLabeledSeoMetricRows(
                      dadosUnificadoSEOChart,
                      allMetrics,
                    );
                    return (
                      <TremorLineChart
                        className="h-72"
                        data={labeledData}
                        index="data"
                        categories={active.map((m) => m.label)}
                        colors={active.map((m) => m.color)}
                        valueFormatter={(value) =>
                          value.toLocaleString("pt-BR")
                        }
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    );
                  })()
                )}
              </CardContent>
            </Card>

            {/* TABELA DE PALAVRAS-CHAVE (MARCA) */}
            <StandardizedTable
              title="Palavras Chaves (Search Console)"
              columns={colunasPalavrasChave}
              data={palavrasChaveDaMarca}
              // <auditoria-18> O rodapé vem da rota, agregado sobre **todas** as consultas
              // de marca — e não sobre as que couberam na tela. Era `computeTotal`, que
              // M-232 pôs aqui como remendo enquanto o filtro de marca não fosse recorte de
              // verdade no servidor; agora é, e o rodapé fecha com os cards de marca.
              total={palavrasChaveDaMarcaTotal}
              loading={seoCardsLoading}
              className="mt-4"
            />

            {/* Gráficos de Dispositivos - rótulos em percentual de participação */}
            <div className="grid gap-6 md:grid-cols-4">
              {(() => {
                const PIE_COLORS = [
                  "#3b82f6",
                  "#10b981",
                  "#f59e0b",
                  "#ef4444",
                  "#8b5cf6",
                ];
                const MIN_LABEL_PERCENT = 5;
                const createPieLabelRenderer =
                  () =>
                  ({
                    cx,
                    cy,
                    midAngle,
                    innerRadius,
                    outerRadius,
                    percent,
                  }: PieLabelProps) => {
                    const percentage = Number(percent || 0) * 100;
                    if (
                      !Number.isFinite(percentage) ||
                      percentage < MIN_LABEL_PERCENT
                    )
                      return null;
                    const RADIAN = Math.PI / 180;
                    const radius =
                      innerRadius + (outerRadius - innerRadius) * 0.55;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill="#fff"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={12}
                        fontWeight={600}
                      >
                        {`${percentage.toFixed(1)}%`}
                      </text>
                    );
                  };
                const renderPieLegend = (
                  data: { name: string; value: number }[],
                ) => (
                  <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2 text-xs">
                    {data.map((entry, i) => (
                      <span
                        key={entry.name}
                        className="inline-flex items-center gap-1 text-muted-foreground"
                      >
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{
                            backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                          }}
                        />
                        {entry.name}
                      </span>
                    ))}
                  </div>
                );
                return brandDeviceChartsConfig.map((chart) => (
                  <Card className="card-kpi-secondary" key={chart.title}>
                    <CardHeader>
                      <CardTitle>{chart.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {seoBrandDevicesLoading ? (
                        <div className="h-40 bg-muted animate-pulse rounded" />
                      ) : (
                        <>
                          <div className="mx-auto h-[200px] w-full max-w-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={chart.data}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={85}
                                  innerRadius={0}
                                  label={createPieLabelRenderer()}
                                  labelLine={false}
                                  isAnimationActive={true}
                                  stroke="hsl(var(--background))"
                                  strokeWidth={2}
                                >
                                  {chart.data.map(
                                    (_: SEODeviceChartItem, index: number) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={
                                          PIE_COLORS[index % PIE_COLORS.length]
                                        }
                                      />
                                    ),
                                  )}
                                </Pie>
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length)
                                      return null;
                                    const entry = payload[0];
                                    const total = chart.data.reduce(
                                      (s, d) => s + d.value,
                                      0,
                                    );
                                    const pct =
                                      total > 0
                                        ? (
                                            (Number(entry.value) / total) *
                                            100
                                          ).toFixed(1)
                                        : "0";
                                    return (
                                      <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                                        <p className="font-medium text-foreground">
                                          {entry.name}
                                        </p>
                                        <p className="text-muted-foreground">
                                          {chart.formatValue?.(
                                            Number(entry.value),
                                          ) ?? entry.value}{" "}
                                          ({pct}%)
                                        </p>
                                      </div>
                                    );
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          {renderPieLegend(chart.data)}
                        </>
                      )}
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>

            {/* Tabela de Dispositivos */}
            <StandardizedTable
              title="Categoria do Dispositivo"
              columns={colunasDispositivo}
              data={dadosTabelaDispositivoMarca}
              loading={seoBrandDevicesLoading}
              className="mt-4"
            />
          </FeatureSectionPaywall>
        </TabsContent>

        {/* ====== ABA PÁGINAS DE ENTRADA ====== */}
        <TabsContent value="paginas-entrada" className="space-y-6 mt-4">
          <FeatureSectionPaywall
            featureSlug={SLUGS["analytics:seo-pagina-de-entrada"]}
            description="Análise de desempenho das páginas de entrada de SEO."
            planName={planName}
            isLocked={
              !isSeoFeatureEnabled(SLUGS["analytics:seo-pagina-de-entrada"])
            }
          >
            {/* PRIMEIRA LINHA: CARDS PRINCIPAIS (mesmos da HOME) */}
            <div className="grid gap-6 md:grid-cols-3">
              {/* Pedidos Orgânicos */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">Pedidos Orgânicos</span>
                      <HelpTooltip
                        content={getKpiTooltip("Pedidos Orgânicos")}
                        triggerClassName="h-3.5 w-3.5 text-[9px]"
                      />
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-3xl" />
                  ) : seoCardsData?.pedidos_organicos ? (
                    <>
                      <div className="flex items-baseline gap-2 mb-4 min-w-0">
                        <div className="min-w-0 flex-1">
                          {seoScalarNumber(
                            seoCardsData.pedidos_organicos.valor,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-3xl font-light text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            <EstadoDoValor
                              carregando={false}
                              disponivel
                              fontes={FONTES.ga4}
                              className="text-3xl font-light text-muted-foreground"
                            >
                              <KpiNumber
                                value={Number(
                                  seoScalarNumber(
                                    seoCardsData.pedidos_organicos.valor,
                                  ) ?? 0,
                                )}
                                format={(n) => formatNumber(n)}
                                className="font-light text-foreground"
                                maxFontPx={40}
                                breathingRoomPx={24}
                                ariaLabel="Pedidos orgânicos"
                              />
                            </EstadoDoValor>
                          )}
                        </div>
                        {comparisonEnabled &&
                          seoScalarNumber(
                            seoCardsData.pedidos_organicos.valor,
                          ) !== null &&
                          typeof seoCardsData.pedidos_organicos
                            .change_percentage === "number" &&
                          seoCardsData.pedidos_organicos.trend_indicator && (
                            <span
                              className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.pedidos_organicos.trend_indicator)}`}
                            >
                              <IconeDaTendencia
                                tendencia={
                                  seoCardsData.pedidos_organicos.trend_indicator
                                }
                                className="w-4 h-4"
                              />
                              {formatSignedPercent(
                                seoCardsData.pedidos_organicos
                                  .change_percentage,
                              )}
                            </span>
                          )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            Representatividade
                          </span>
                          <span className="text-sm font-medium">
                            {seoCardsData.pedidos_organicos.representatividade}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            Pedidos Totais
                          </span>
                          <span className="text-sm font-medium">
                            {seoScalarNumber(
                              seoCardsData.pedidos_organicos.pedidos_totais,
                            ) === null ? (
                              <ValorIndisponivel
                                className="text-muted-foreground"
                                fontes={FONTES.ga4}
                              />
                            ) : (
                              formatNumber(
                                seoScalarNumber(
                                  seoCardsData.pedidos_organicos.pedidos_totais,
                                ) ?? 0,
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <ValorIndisponivel
                      className="text-3xl font-light text-muted-foreground"
                      fontes={FONTES.ga4}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Sessões Orgânicas */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">Sessões Orgânicas</span>
                      <HelpTooltip
                        content={getKpiTooltip("Sessões Orgânicas")}
                        triggerClassName="h-3.5 w-3.5 text-[9px]"
                      />
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-3xl" />
                  ) : seoCardsData?.sessoes_organicas ? (
                    <>
                      <div className="flex items-baseline gap-2 mb-4 min-w-0">
                        <div className="min-w-0 flex-1">
                          {seoScalarNumber(
                            seoCardsData.sessoes_organicas.valor,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-3xl font-light text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            <EstadoDoValor
                              carregando={false}
                              disponivel
                              fontes={FONTES.ga4}
                              className="text-3xl font-light text-muted-foreground"
                            >
                              <KpiNumber
                                value={Number(
                                  seoScalarNumber(
                                    seoCardsData.sessoes_organicas.valor,
                                  ) ?? 0,
                                )}
                                format={(n) => formatNumber(n)}
                                className="font-light text-foreground"
                                maxFontPx={40}
                                breathingRoomPx={24}
                                ariaLabel="Sessões orgânicas"
                              />
                            </EstadoDoValor>
                          )}
                        </div>
                        {comparisonEnabled &&
                          seoScalarNumber(
                            seoCardsData.sessoes_organicas.valor,
                          ) !== null &&
                          typeof seoCardsData.sessoes_organicas
                            .change_percentage === "number" &&
                          seoCardsData.sessoes_organicas.trend_indicator && (
                            <span
                              className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.sessoes_organicas.trend_indicator)}`}
                            >
                              <IconeDaTendencia
                                tendencia={
                                  seoCardsData.sessoes_organicas.trend_indicator
                                }
                                className="w-4 h-4"
                              />
                              {formatSignedPercent(
                                seoCardsData.sessoes_organicas
                                  .change_percentage,
                              )}
                            </span>
                          )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            Representatividade
                          </span>
                          <span className="text-sm font-medium">
                            {seoCardsData.sessoes_organicas.representatividade}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            Sessões Totais
                          </span>
                          <span className="text-sm font-medium">
                            {seoScalarNumber(
                              seoCardsData.sessoes_organicas.sessoes_totais,
                            ) === null ? (
                              <ValorIndisponivel
                                className="text-muted-foreground"
                                fontes={FONTES.ga4}
                              />
                            ) : (
                              formatNumber(
                                seoScalarNumber(
                                  seoCardsData.sessoes_organicas.sessoes_totais,
                                ) ?? 0,
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <ValorIndisponivel
                      className="text-3xl font-light text-muted-foreground"
                      fontes={FONTES.ga4}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Taxa Conversão Orgânica */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">Taxa Conversão Orgânica</span>
                      <HelpTooltip
                        content={getKpiTooltip("Taxa Conversão Orgânica")}
                        triggerClassName="h-3.5 w-3.5 text-[9px]"
                      />
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-3xl" />
                  ) : seoCardsData?.taxa_conversao_organica ? (
                    <>
                      <div className="flex items-baseline gap-2 mb-4 min-w-0">
                        <div className="min-w-0 flex-1">
                          {seoScalarNumber(
                            seoCardsData.taxa_conversao_organica.valor,
                          ) === null ? (
                            <ValorIndisponivel
                              className="text-3xl font-light text-muted-foreground"
                              fontes={FONTES.ga4}
                            />
                          ) : (
                            <EstadoDoValor
                              carregando={false}
                              disponivel
                              fontes={FONTES.ga4}
                              className="text-3xl font-light text-muted-foreground"
                            >
                              <KpiNumber
                                value={Number(
                                  seoScalarNumber(
                                    seoCardsData.taxa_conversao_organica.valor,
                                  ) ?? 0,
                                )}
                                format={(n) => `${Number(n).toFixed(2)}%`}
                                className="font-light text-foreground"
                                maxFontPx={40}
                                breathingRoomPx={24}
                                ariaLabel="Taxa de conversão orgânica"
                              />
                            </EstadoDoValor>
                          )}
                        </div>
                        {comparisonEnabled &&
                          seoScalarNumber(
                            seoCardsData.taxa_conversao_organica.valor,
                          ) !== null &&
                          typeof seoCardsData.taxa_conversao_organica
                            .change_percentage === "number" &&
                          seoCardsData.taxa_conversao_organica
                            .trend_indicator && (
                            <span
                              className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.taxa_conversao_organica.trend_indicator)}`}
                            >
                              <IconeDaTendencia
                                tendencia={
                                  seoCardsData.taxa_conversao_organica
                                    .trend_indicator
                                }
                                className="w-4 h-4"
                              />
                              {formatSignedPercent(
                                seoCardsData.taxa_conversao_organica
                                  .change_percentage,
                              )}
                            </span>
                          )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            TX Conversão Geral
                          </span>
                          <span className="text-sm font-medium">
                            {seoScalarNumber(
                              seoCardsData.taxa_conversao_organica
                                .taxa_conversao_geral,
                            ) === null ? (
                              <ValorIndisponivel
                                className="text-muted-foreground"
                                fontes={FONTES.ga4}
                              />
                            ) : (
                              `${seoScalarNumber(seoCardsData.taxa_conversao_organica.taxa_conversao_geral) ?? 0}%`
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <ValorIndisponivel
                      className="text-3xl font-light text-muted-foreground"
                      fontes={FONTES.ga4}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* SEGUNDA LINHA: CARDS SEARCH CONSOLE */}
            <div className="grid gap-6 md:grid-cols-4">
              {/* Total de Clicks */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">
                        Total de Clicks (Search Console)
                      </span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.total_clicks_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.total_clicks_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.total_clicks_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={30}
                          breathingRoomPx={18}
                          ariaLabel="Total de clicks"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.total_clicks_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.total_clicks_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Total de Clicks MARCA */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">
                        Total de Clicks MARCA (Search Console)
                      </span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.total_clicks_marca_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.total_clicks_marca_search_console
                              .valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.total_clicks_marca_search_console
                              .valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={30}
                          breathingRoomPx={18}
                          ariaLabel="Total de clicks marca"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.total_clicks_marca_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.total_clicks_marca_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.total_clicks_marca_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.total_clicks_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Impressões */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">
                        Impressões (Search Console)
                      </span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.impressoes_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.impressoes_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.impressoes_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={30}
                          breathingRoomPx={18}
                          ariaLabel="Impressões"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.impressoes_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.impressoes_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Impressões MARCA */}
              <Card className="card-kpi-primary">
                <CardHeader>
                  <CardTitle>
                    <span className="inline-flex items-center gap-1">
                      <span className="truncate">
                        Impressões MARCA (Search Console)
                      </span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seoCardsLoading ? (
                    <CarregandoValor className="text-2xl" />
                  ) : seoCardsData?.impressoes_marca_search_console ? (
                    <div className="flex flex-col items-start">
                      <EstadoDoValor
                        carregando={false}
                        disponivel={
                          seoScalarNumber(
                            seoCardsData.impressoes_marca_search_console.valor,
                          ) !== null
                        }
                        fontes={FONTES.searchConsole}
                        className="text-muted-foreground text-sm"
                      >
                        <KpiNumber
                          value={Number(
                            seoCardsData.impressoes_marca_search_console.valor,
                          )}
                          format={(n) => formatNumber(n)}
                          className="font-light text-foreground"
                          maxFontPx={30}
                          breathingRoomPx={18}
                          ariaLabel="Impressões marca"
                        />
                      </EstadoDoValor>
                      {comparisonEnabled &&
                        typeof seoCardsData.impressoes_marca_search_console
                          .change_percentage === "number" && (
                          <span
                            className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${corDaTendencia(seoCardsData.impressoes_marca_search_console.trend_indicator)}`}
                          >
                            <IconeDaTendencia
                              tendencia={
                                seoCardsData.impressoes_marca_search_console
                                  .trend_indicator
                              }
                              className="w-4 h-4"
                            />
                            {formatSignedPercent(
                              seoCardsData.impressoes_marca_search_console
                                .change_percentage,
                            )}
                          </span>
                        )}
                    </div>
                  ) : (
                    <ValorIndisponivel
                      className="text-muted-foreground text-sm"
                      fontes={FONTES.searchConsole}
                      erro={seoCardsError !== null}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* TABELA PÁGINA DE ENTRADA */}
            {/* A tabela fica sempre montada, e o carregamento é dela: trocá-la por um
                placeholder apagava a busca digitada, que o servidor continuava aplicando. */}
            {
              // A paginação, a busca e a ordenação são do servidor, como na tabela
              // irmã de `/dashboard/pages`. Antes o componente repartia por conta
              // própria as 25 linhas recebidas em páginas de 10 e desenhava um
              // segundo rodapé de paginação por cima do widget manual — três
              // contagens diferentes na mesma tabela —, e o `sortable: true` das
              // colunas reordenava só a página, e não o conjunto.
              <StandardizedTable
                title="PÁGINA DE ENTRADA"
                columns={colunasLandingPage}
                data={landingPageTableData}
                total={landingPageTotalRow}
                loading={lpLoading}
                className="mt-4"
                pagination={{
                  current: lpPage,
                  pageSize: lpPageSize,
                  total: lpPagination.total_items,
                  onPageChange: (page) => setLpPage(page),
                }}
                queryMode="server"
                onQueryChange={(q) => {
                  setLpTableQuery(q);
                  setLpPage(1);
                }}
              />
            }

            {/* GRÁFICO SESSÕES ORGÂNICAS */}
            <Card className="card-kpi-secondary">
              <CardHeader>
                <CardTitle>Sessões Orgânicas</CardTitle>
              </CardHeader>
              <CardContent>
                {seoCardsLoading ? (
                  <div className="h-72 bg-muted animate-pulse rounded" />
                ) : (
                  <TremorLineChart
                    className="h-72"
                    data={dadosSessoesGraficos}
                    index="data"
                    categories={["Sessões Orgânicas"]}
                    colors={["amber"]}
                    valueFormatter={(value) => value.toLocaleString("pt-BR")}
                    showLegend={true}
                    showGridLines={true}
                    showAnimation={true}
                  />
                )}
              </CardContent>
            </Card>
          </FeatureSectionPaywall>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEOPage;
